import { useCallback, useEffect, useState } from 'react';
import { Assessment, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';

import * as CensoAPI from '../../../api/censoPopulacional';
import * as DataListAPI from '../../../api/dataList';
import * as UnidadeAPI from '../../../api/unidadeCondominial';
import * as UnidadePessoaAPI from '../../../api/unidadeCondominialPessoa';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import useFlashMessage from '../../../hooks/useFlashMessage';
import DataList from '../../form/DataList';

const FINALIDADES = [
    ['RESIDENCIAL', 'Residencial'],
    ['COMERCIAL', 'Comercial'],
    ['MISTA', 'Mista'],
    ['INSTITUCIONAL', 'Institucional'],
    ['SEM_EDIFICACAO', 'Sem edificação'],
    ['NAO_INFORMADA', 'Não informada'],
];
const ORIGENS = [
    ['POCOS_CONDOMINIO', 'Poços do condomínio'],
    ['CONCESSIONARIA', 'Concessionária'],
    ['MISTA', 'Mista'],
    ['SEM_ABASTECIMENTO', 'Sem abastecimento'],
    ['NAO_INFORMADO', 'Não informado'],
];
const anoAtual = new Date().getFullYear();

const TITULOS_FLUTUANTE = {
    funcionariosFixos: 'Quantidade de pessoas que trabalham regularmente no estabelecimento durante o ano informado.',
    usuariosDiaMedia: 'Média de clientes, alunos, visitantes ou usuários que passam pelo estabelecimento em um dia normal.',
    usuariosDiaPico: 'Maior quantidade estimada ou medida de usuários presentes ao longo de um único dia.',
    diasFuncionamentoAno: 'Quantidade de dias em que o estabelecimento funcionou ou deverá funcionar durante o ano.',
};

const vazioAbastecimento = (empresaId, unidadeCondominialId) => ({
    empresaId,
    unidadeCondominialId,
    dataInicio: '',
    dataFim: '',
    origem: 'NAO_INFORMADO',
    aguaResidencial: false,
    aguaComercial: false,
    fonteInformacao: 'CADASTRO',
    documentoReferencia: '',
    observacao: '',
});
const vazioFlutuante = (empresaId, unidadeCondominialId) => ({
    empresaId,
    unidadeCondominialId,
    pessoaId: null,
    ano: anoAtual,
    Pessoa: { descricao: '' },
    atividade: '',
    funcionariosFixos: 0,
    usuariosDiaMedia: 0,
    usuariosDiaPico: 0,
    diasFuncionamentoAno: 0,
    fonteInformacao: 'DECLARADO',
    documentoReferencia: '',
    observacao: '',
});

function Index() {
    const { empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const [tab, setTab] = useState(0);
    const [unidades, setUnidades] = useState([]);
    const [optionsUnidade, setOptionsUnidade] = useState([]);
    const [unidadeId, setUnidadeId] = useState('');
    const [unidadeBusca, setUnidadeBusca] = useState('');
    const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);
    const [abastecimentos, setAbastecimentos] = useState([]);
    const [flutuantes, setFlutuantes] = useState([]);
    const [dialogAbastecimento, setDialogAbastecimento] = useState(null);
    const [dialogFlutuante, setDialogFlutuante] = useState(null);
    const [pessoas, setPessoas] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [periodo, setPeriodo] = useState({ anoInicio: 2017, anoFim: anoAtual });

    const tratarErro = useCallback(
        (error, padrao) => {
            setFlashMessage(error.response?.data?.message || padrao, error.response?.data?.type || 'error');
        },
        [setFlashMessage]
    );

    const carregar = useCallback(async () => {
        try {
            const [u, a, f] = await Promise.all([
                UnidadeAPI.listar({
                    start: 0,
                    size: 2000,
                    sorting: JSON.stringify([{ id: 'codigo', desc: false }]),
                }),
                CensoAPI.listarAbastecimentos({ empresaId }),
                CensoAPI.listarFlutuante({ empresaId, anoInicio: periodo.anoInicio, anoFim: periodo.anoFim }),
            ]);
            setUnidades((u.data?.data || []).filter((item) => Number(item.empresaId) === Number(empresaId)));
            setAbastecimentos(a.data?.data || []);
            setFlutuantes(f.data?.data || []);
        } catch (error) {
            tratarErro(error, 'Não foi possível carregar os dados do censo.');
        }
    }, [empresaId, periodo.anoFim, periodo.anoInicio, tratarErro]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    async function consultarUnidades(filtro = '') {
        try {
            const result = await DataListAPI.dataList({
                empresaId,
                tabela: 'UnidadeCondominial',
                filtro,
            });

            const opcoes = Array.isArray(result) ? result : [];
            setOptionsUnidade(opcoes);
            return opcoes;
        } catch (error) {
            setOptionsUnidade([]);
            tratarErro(error, 'Não foi possível consultar as unidades condominiais.');
            return [];
        }
    }

    async function selecionarUnidade(event) {
        const valorSelecionado = String(event.target.value || '').trim();
        const opcaoSelecionada = optionsUnidade.find((opcao) => String(opcao?.descricao || '').trim() === valorSelecionado);

        if (opcaoSelecionada) {
            const unidadeCompleta = unidades.find((item) => Number(item.id) === Number(opcaoSelecionada.id));

            if (!unidadeCompleta) {
                setUnidadeId('');
                setUnidadeSelecionada(null);
                setFlashMessage('A unidade selecionada não foi localizada na empresa atual.', 'warning');
                return;
            }

            setUnidadeBusca(opcaoSelecionada.descricao);
            setUnidadeId(Number(opcaoSelecionada.id));
            setUnidadeSelecionada(unidadeCompleta);
            return;
        }

        setUnidadeBusca(valorSelecionado);
        setUnidadeId('');
        setUnidadeSelecionada(null);
        await consultarUnidades(valorSelecionado);
    }

    async function salvarFinalidade() {
        if (!unidadeSelecionada) return setFlashMessage('Selecione uma unidade.', 'warning');
        setSalvando(true);
        try {
            const response = await CensoAPI.atualizarFinalidade({
                empresaId,
                unidadeCondominialId: unidadeSelecionada.id,
                finalidade: unidadeSelecionada.finalidade,
                atividadePrincipal: unidadeSelecionada.atividadePrincipal,
            });
            setFlashMessage(response.data.message, response.data.type);
            setUnidadeSelecionada(response.data?.data || unidadeSelecionada);
            await carregar();
        } catch (error) {
            tratarErro(error, 'Não foi possível atualizar a finalidade.');
        } finally {
            setSalvando(false);
        }
    }

    async function consultarPessoas(filtro) {
        try {
            const result = await DataListAPI.dataList({ empresaId, tabela: 'Pessoa', filtro });
            setPessoas(result.filter((item) => !item.tipo || item.tipo === 'Jurídica'));
            return result;
        } catch (error) {
            tratarErro(error, 'Não foi possível consultar as pessoas jurídicas.');
            return [];
        }
    }

    async function salvarAbastecimento() {
        setSalvando(true);
        try {
            const response = await CensoAPI.salvarAbastecimento(dialogAbastecimento);
            setFlashMessage(response.data.message, response.data.type);
            setDialogAbastecimento(null);
            await carregar();
        } catch (error) {
            tratarErro(error, 'Não foi possível salvar o abastecimento.');
        } finally {
            setSalvando(false);
        }
    }

    async function salvarFlutuante() {
        setSalvando(true);
        try {
            const response = await CensoAPI.salvarFlutuante(dialogFlutuante);
            setFlashMessage(response.data.message, response.data.type);
            setDialogFlutuante(null);
            await carregar();
        } catch (error) {
            tratarErro(error, 'Não foi possível salvar a população flutuante.');
        } finally {
            setSalvando(false);
        }
    }

    async function excluir(tipo, uuid) {
        if (!window.confirm('Confirma a exclusão deste registro?')) return;
        try {
            const response =
                tipo === 'abastecimento' ? await CensoAPI.excluirAbastecimento(uuid) : await CensoAPI.excluirFlutuante(uuid);
            setFlashMessage(response.data.message, response.data.type);
            await carregar();
        } catch (error) {
            tratarErro(error, 'Não foi possível excluir o registro.');
        }
    }

    async function gerarPdf() {
        const janela = window.open('', '_blank');
        if (!janela) return setFlashMessage('Permita pop-ups para gerar o relatório.', 'warning');
        try {
            const response = await UnidadePessoaAPI.censo({ empresaId, ...periodo });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            janela.location.href = url;
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            janela.close();
            tratarErro(error, 'Não foi possível gerar o censo.');
        }
    }

    const alterarUnidade = (campo, valor) =>
        setUnidadeSelecionada((anterior) => (anterior ? { ...anterior, [campo]: valor } : anterior));

    return (
        <Box sx={{ p: 2 }}>
            <h1>Censo populacional</h1>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                    <DataList
                        text="Unidade"
                        name="UnidadeCondominial.descricao"
                        title="Digite o código, quadra, lote ou descrição e selecione a unidade que será classificada."
                        inputWidth="420px"
                        filteredOptions={optionsUnidade}
                        value={unidadeBusca}
                        handleOnFocus={(event) => consultarUnidades(event.target.value)}
                        handleOnChange={selecionarUnidade}
                    />
                    <TextField
                        label="Ano inicial"
                        title="Primeiro ano que será carregado e apresentado no relatório do censo."
                        type="number"
                        value={periodo.anoInicio}
                        onChange={(e) => setPeriodo({ ...periodo, anoInicio: e.target.value })}
                    />
                    <TextField
                        label="Ano final"
                        title="Último ano que será carregado e apresentado no relatório do censo."
                        type="number"
                        value={periodo.anoFim}
                        onChange={(e) => setPeriodo({ ...periodo, anoFim: e.target.value })}
                    />
                    <Button
                        title="Recarrega os cadastros conforme o período anual informado."
                        color="info"
                        variant="contained"
                        onClick={carregar}
                    >
                        Atualizar
                    </Button>
                    <Button
                        title="Gera o PDF detalhado com moradores, população flutuante, abastecimento e divergências cadastrais."
                        variant="contained"
                        startIcon={<Assessment />}
                        onClick={gerarPdf}
                    >
                        Gerar censo
                    </Button>
                </Stack>
            </Paper>

            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                <Tab
                    title="Classifica a utilização atual do lote, independentemente da origem ou categoria da água."
                    label="Finalidade"
                />
                <Tab
                    title="Registra por período se a água vem dos poços, da concessionária, de ambas ou se não há abastecimento."
                    label="Abastecimento"
                />
                <Tab
                    title="Registra anualmente funcionários e fluxo de clientes, alunos, visitantes ou outros usuários."
                    label="População flutuante"
                />
            </Tabs>

            {tab === 0 && (
                <Paper sx={{ p: 2 }}>
                    <Stack spacing={2} sx={{ maxWidth: 650 }}>
                        <TextField
                            select
                            label="Finalidade do lote"
                            title="Uso predominante do imóvel: residencial, comercial, misto, institucional ou terreno sem edificação."
                            value={unidadeSelecionada?.finalidade || 'NAO_INFORMADA'}
                            disabled={!unidadeSelecionada}
                            onChange={(e) => alterarUnidade('finalidade', e.target.value)}
                        >
                            {FINALIDADES.map(([v, l]) => (
                                <MenuItem key={v} value={v}>
                                    {l}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Atividade principal"
                            value={unidadeSelecionada?.atividadePrincipal || ''}
                            disabled={!unidadeSelecionada}
                            onChange={(e) => alterarUnidade('atividadePrincipal', e.target.value)}
                            title="Obrigatória para finalidade comercial, mista ou institucional"
                        />
                        <Button
                            title="Salva a classificação atual do lote e sua atividade principal."
                            variant="contained"
                            onClick={salvarFinalidade}
                            disabled={!unidadeSelecionada || salvando}
                        >
                            Salvar finalidade
                        </Button>
                    </Stack>
                </Paper>
            )}

            {tab === 1 && (
                <Paper sx={{ p: 2 }}>
                    <Button
                        title="Inicia um novo período histórico de origem e categoria da água desta unidade."
                        variant="contained"
                        disabled={!unidadeSelecionada}
                        onClick={() => setDialogAbastecimento(vazioAbastecimento(empresaId, unidadeSelecionada.id))}
                    >
                        Novo período
                    </Button>
                    {abastecimentos
                        .filter((r) => !unidadeId || Number(r.unidadeCondominialId) === Number(unidadeId))
                        .map((r) => (
                            <Stack
                                key={r.uuid}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ py: 1, borderBottom: '1px solid #ddd' }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    {r.UnidadeCondominial?.codigo} | {r.dataInicio} a {r.dataFim || 'atual'} | {r.origem} |
                                    Residencial: {r.aguaResidencial ? 'Sim' : 'Não'} | Comercial:{' '}
                                    {r.aguaComercial ? 'Sim' : 'Não'}
                                </Box>
                                <IconButton
                                    title="Editar este período de abastecimento"
                                    onClick={() => setDialogAbastecimento({ ...r })}
                                >
                                    <Edit />
                                </IconButton>
                                <IconButton
                                    title="Excluir este período de abastecimento"
                                    color="error"
                                    onClick={() => excluir('abastecimento', r.uuid)}
                                >
                                    <Delete />
                                </IconButton>
                            </Stack>
                        ))}
                </Paper>
            )}

            {tab === 2 && (
                <Paper sx={{ p: 2 }}>
                    <Button
                        title="Informa funcionários e fluxo médio/pico do estabelecimento em determinado ano."
                        variant="contained"
                        disabled={!unidadeSelecionada}
                        onClick={() => setDialogFlutuante(vazioFlutuante(empresaId, unidadeSelecionada.id))}
                    >
                        Nova declaração anual
                    </Button>
                    {flutuantes
                        .filter((r) => !unidadeId || Number(r.unidadeCondominialId) === Number(unidadeId))
                        .map((r) => (
                            <Stack
                                key={r.uuid}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ py: 1, borderBottom: '1px solid #ddd' }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    {r.ano} | {r.UnidadeCondominial?.codigo} | {r.Pessoa?.descricao || 'Sem PJ vinculada'} |{' '}
                                    {r.atividade} | Funcionários: {r.funcionariosFixos} | Média/dia: {r.usuariosDiaMedia} | Pico:{' '}
                                    {r.usuariosDiaPico}
                                </Box>
                                <IconButton
                                    title="Editar esta declaração anual"
                                    onClick={() => setDialogFlutuante({ ...r, Pessoa: r.Pessoa || { descricao: '' } })}
                                >
                                    <Edit />
                                </IconButton>
                                <IconButton
                                    title="Excluir esta declaração anual"
                                    color="error"
                                    onClick={() => excluir('flutuante', r.uuid)}
                                >
                                    <Delete />
                                </IconButton>
                            </Stack>
                        ))}
                </Paper>
            )}

            <Dialog
                open={Boolean(dialogAbastecimento)}
                onClose={() => !salvando && setDialogAbastecimento(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Histórico de abastecimento</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Início"
                                title="Primeiro dia em que esta forma de abastecimento passou a valer."
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={dialogAbastecimento?.dataInicio || ''}
                                onChange={(e) => setDialogAbastecimento({ ...dialogAbastecimento, dataInicio: e.target.value })}
                            />
                            <TextField
                                label="Término"
                                title="Último dia desta forma de abastecimento. Deixe vazio quando ainda estiver vigente."
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={dialogAbastecimento?.dataFim || ''}
                                onChange={(e) => setDialogAbastecimento({ ...dialogAbastecimento, dataFim: e.target.value })}
                            />
                        </Stack>
                        <TextField
                            select
                            label="Origem"
                            title="Identifica de onde vem a água: poços do condomínio, concessionária, ambas ou nenhuma."
                            value={dialogAbastecimento?.origem || 'NAO_INFORMADO'}
                            onChange={(e) => setDialogAbastecimento({ ...dialogAbastecimento, origem: e.target.value })}
                        >
                            {ORIGENS.map(([v, l]) => (
                                <MenuItem key={v} value={v}>
                                    {l}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Stack direction="row">
                            <FormControlLabel
                                title="Marque quando a unidade possui ligação dos poços classificada como água residencial."
                                control={
                                    <Checkbox
                                        checked={Boolean(dialogAbastecimento?.aguaResidencial)}
                                        onChange={(e) =>
                                            setDialogAbastecimento({ ...dialogAbastecimento, aguaResidencial: e.target.checked })
                                        }
                                    />
                                }
                                label="Água residencial"
                            />
                            <FormControlLabel
                                title="Marque quando a unidade possui ligação dos poços classificada como água comercial."
                                control={
                                    <Checkbox
                                        checked={Boolean(dialogAbastecimento?.aguaComercial)}
                                        onChange={(e) =>
                                            setDialogAbastecimento({ ...dialogAbastecimento, aguaComercial: e.target.checked })
                                        }
                                    />
                                }
                                label="Água comercial"
                            />
                        </Stack>
                        <TextField
                            label="Documento de referência"
                            title="Número, nome ou identificação do cadastro, conta, processo, declaração ou documento que comprova a informação."
                            value={dialogAbastecimento?.documentoReferencia || ''}
                            onChange={(e) =>
                                setDialogAbastecimento({ ...dialogAbastecimento, documentoReferencia: e.target.value })
                            }
                        />
                        <TextField
                            label="Observação"
                            title="Registre exceções, ligações compartilhadas, obras, alterações ou esclarecimentos relevantes."
                            multiline
                            value={dialogAbastecimento?.observacao || ''}
                            onChange={(e) => setDialogAbastecimento({ ...dialogAbastecimento, observacao: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button title="Fecha sem gravar as alterações." onClick={() => setDialogAbastecimento(null)}>
                        Cancelar
                    </Button>
                    <Button
                        title="Salva o período de abastecimento informado."
                        variant="contained"
                        disabled={salvando}
                        onClick={salvarAbastecimento}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(dialogFlutuante)} onClose={() => !salvando && setDialogFlutuante(null)} maxWidth="md" fullWidth>
                <DialogTitle>População flutuante anual</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <DataList
                            text="Pessoa jurídica"
                            title="Estabelecimento responsável pela atividade. Pode ficar vazio quando o responsável histórico ainda não for conhecido."
                            name="Pessoa.descricao"
                            filteredOptions={pessoas}
                            value={dialogFlutuante?.Pessoa?.descricao || ''}
                            handleOnFocus={(e) => consultarPessoas(e.target.value)}
                            handleOnChange={async (e) => {
                                const opcoes = await consultarPessoas(e.target.value);
                                const selecionada = opcoes.find((o) => String(o.descricao || o.label) === e.target.value);
                                setDialogFlutuante({
                                    ...dialogFlutuante,
                                    pessoaId: selecionada?.id || null,
                                    Pessoa: selecionada || { descricao: e.target.value },
                                });
                            }}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Ano"
                                title="Ano civil ao qual esta declaração de população flutuante pertence."
                                type="number"
                                value={dialogFlutuante?.ano || anoAtual}
                                onChange={(e) => setDialogFlutuante({ ...dialogFlutuante, ano: e.target.value })}
                            />
                            <TextField
                                label="Atividade"
                                title="Atividade exercida no ano, como padaria, escola, academia ou galeria."
                                fullWidth
                                value={dialogFlutuante?.atividade || ''}
                                onChange={(e) => setDialogFlutuante({ ...dialogFlutuante, atividade: e.target.value })}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            {[
                                ['funcionariosFixos', 'Funcionários fixos'],
                                ['usuariosDiaMedia', 'Média de usuários/dia'],
                                ['usuariosDiaPico', 'Pico diário'],
                                ['diasFuncionamentoAno', 'Dias/ano'],
                            ].map(([campo, label]) => (
                                <TextField
                                    key={campo}
                                    label={label}
                                    title={TITULOS_FLUTUANTE[campo]}
                                    type="number"
                                    value={dialogFlutuante?.[campo] ?? 0}
                                    onChange={(e) => setDialogFlutuante({ ...dialogFlutuante, [campo]: e.target.value })}
                                />
                            ))}
                        </Stack>
                        <TextField
                            select
                            label="Fonte"
                            title="Indica como os números foram obtidos: declaração, estimativa, medição ou documento histórico."
                            value={dialogFlutuante?.fonteInformacao || 'DECLARADO'}
                            onChange={(e) => setDialogFlutuante({ ...dialogFlutuante, fonteInformacao: e.target.value })}
                        >
                            {['DECLARADO', 'ESTIMADO', 'MEDIDO', 'DOCUMENTO'].map((v) => (
                                <MenuItem key={v} value={v}>
                                    {v}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Documento de referência"
                            title="Identificação da declaração, planilha, processo, relatório ou outro documento usado como fonte."
                            value={dialogFlutuante?.documentoReferencia || ''}
                            onChange={(e) => setDialogFlutuante({ ...dialogFlutuante, documentoReferencia: e.target.value })}
                        />
                        <TextField
                            label="Observação"
                            title="Informe sazonalidade, turnos, períodos sem funcionamento e outras particularidades do fluxo."
                            multiline
                            value={dialogFlutuante?.observacao || ''}
                            onChange={(e) => setDialogFlutuante({ ...dialogFlutuante, observacao: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button title="Fecha sem gravar as alterações." onClick={() => setDialogFlutuante(null)}>
                        Cancelar
                    </Button>
                    <Button
                        title="Salva a declaração anual de população flutuante."
                        variant="contained"
                        disabled={salvando}
                        onClick={salvarFlutuante}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Index;
